import { ReactNode, MouseEvent } from 'react'
import { useNavigate, useParams, Link as RouterLink } from 'react-router-dom'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import Divider from '@mui/material/Divider'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import { EuiPageHeader, EuiTitle } from '@equipmentshare/ds2'
import { useFulfillmentOrderQuery, TransferOrderStatus, FulfillmentOrderStatus, useFulfillmentOrderByOrderNumberQuery } from '../../graphql'
import { formatDateTime } from '../../utils/formatters/date'
import { FO_STATUS_CHIP_COLOR, FO_STATUS_LABEL, FO_TYPE_LABEL } from './constants'
import CancelFulfillmentOrderButton from './components/CancelFulfillmentOrderButton'
import CreatePickOrderButton from './pick-orders/components/CreatePickOrderButton'
import TableHeaderCell from './components/TableHeaderCell'
import getFulfillmentOrderSource from './getFulfillmentOrderSource'

type InfoRowProps = {
	label: string
	value: ReactNode
}

function InfoRow( { label, value }: InfoRowProps ) {
	if ( value === null || value === '' || value === undefined ) return null
	return (
		<Stack direction="row" spacing={2} sx={{ py: 0.5 }}>
			<Typography variant="body2" fontWeight="bold" sx={{ minWidth: 200, flexShrink: 0 }}>
				{label}
			</Typography>
			<Typography variant="body2" component="div">{value}</Typography>
		</Stack>
	)
}

const TO_STATUS_COLOR: Record<string, 'default' | 'warning' | 'success' | 'error' | 'info' | 'primary'> = {
	[ TransferOrderStatus.Pending ]: 'warning',
	[ TransferOrderStatus.Approved ]: 'info',
	[ TransferOrderStatus.InTransit ]: 'primary',
	[ TransferOrderStatus.Completed ]: 'success',
	[ TransferOrderStatus.Rejected ]: 'error',
	[ TransferOrderStatus.Cancelled ]: 'default',
}

const CANCELLABLE_STATUSES = new Set( [
	FulfillmentOrderStatus.Pending,
	FulfillmentOrderStatus.SubmittedForFulfillment,
] )

export default function FulfillmentOrderDetails() {
	const { orderNumber } = useParams<{ orderNumber: string }>()
	const navigate = useNavigate()

	const { data, loading, error } = useFulfillmentOrderByOrderNumberQuery( {
		variables: { orderNumber: orderNumber ?? 0 },
		skip: !orderNumber,
		fetchPolicy: 'network-only',
	} )

	const order = data?.fulfillmentOrderByOrderNumber
	const isCancellable = order ? CANCELLABLE_STATUSES.has( order.status ) : false
	const isCancellationInProgress = order?.status === FulfillmentOrderStatus.CancellationRequested

	return (
		<Box>
			<EuiPageHeader
				pageTitle={<EuiTitle size="m"><span>FO-{order?.orderNumber ?? 'N/A'}</span></EuiTitle>}
				paddingSize="l"
				bottomBorder="extended"
				rightSideItems={order ? [
					...( isCancellable || isCancellationInProgress
						? [ <CancelFulfillmentOrderButton key="cancel" fulfillmentOrderId={order.id} /> ]
						: [] ),
					<CreatePickOrderButton
						key="create-pick-order"
						fulfillmentOrder={{
							id: order.id,
							orderNumber: Number( order.orderNumber ),
							status: order.status,
							lineItems: order.lineItems,
							pickOrders: order.pickOrders,
							fulfillingInventoryId: order.fulfillingInventory.id,
							destinationDescription: order.destinationDescription,
						}}
					/>,
				] : []}
				breadcrumbs={[
					{
						text: 'Fulfillment Orders',
						href: '/dc-admin/fulfillment-orders',
						onClick: ( e: MouseEvent<HTMLElement> ) => {
							e.preventDefault()
							navigate( '/dc-admin/fulfillment-orders' )
						},
					},
					{ text: 'Order Details' },
				]}
			/>

			<Box sx={{ p: 3 }}>
				{loading && (
					<Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
						<CircularProgress />
					</Box>
				)}

				{( error || ( !loading && !order ) ) && (
					<Alert
						severity="error"
						action={
							<Button color="inherit" size="small" onClick={() => navigate( '/dc-admin/fulfillment-orders' )}>
								Back to Fulfillment Orders
							</Button>
						}
					>
						{error?.message ?? 'Fulfillment order not found.'}
					</Alert>
				)}

				{order && (
					<Stack spacing={3}>
						{isCancellationInProgress && (
							<Alert
								severity="warning"
								icon={<CircularProgress size={18} color="inherit" />}
							>
								Cancellation is being processed by Numina. This page will update automatically when complete.
							</Alert>
						)}

						{/* Summary panels */}
						<Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
							{/* Order Info */}
							<Paper variant="outlined" sx={{ flex: 1, p: 2.5 }}>
								<Typography variant="h6" fontWeight={700} gutterBottom>Order Info</Typography>
								<Stack direction="row" spacing={1} alignItems="center" sx={{ py: 0.5 }}>
									<Typography variant="body2" fontWeight="bold" sx={{ minWidth: 200, flexShrink: 0 }}>
										Status
									</Typography>
									<Chip
										label={FO_STATUS_LABEL[ order.status ] ?? order.status}
										color={FO_STATUS_CHIP_COLOR[ order.status ] ?? 'default'}
										size="small"
									/>
								</Stack>
								<InfoRow label="Source" value={getFulfillmentOrderSource( order )} />
								<InfoRow label="Fulfillment Type" value={FO_TYPE_LABEL[ order.fulfillmentType ] ?? order.fulfillmentType} />
								<InfoRow label="Fulfilling DC" value={order.fulfillingInventory.name} />
								<InfoRow label="Destination" value={order.destinationInventory?.name} />
								<InfoRow label="MTT ID" value={order.mttId} />
								<InfoRow label="MTT Quadrant" value={order.mttQuadrant} />
								<InfoRow label="Notes" value={order.notes} />
							</Paper>

							{/* Dates */}
							<Paper variant="outlined" sx={{ flex: 1, p: 2.5 }}>
								<Typography variant="h6" fontWeight={700} gutterBottom>Timeline</Typography>
								<InfoRow label="Created" value={formatDateTime( order.dateCreated )} />
								<InfoRow label="Submitted for Fulfillment" value={formatDateTime( order.dateSubmittedForFulfillment )} />
								<InfoRow label="Completed" value={formatDateTime( order.dateCompleted )} />
								<InfoRow label="Cancellation Requested" value={formatDateTime( order.dateCancellationRequested )} />
								<InfoRow label="Cancelled" value={formatDateTime( order.dateCancelled )} />
							</Paper>

							{/* Contact / Shipping */}
							{( order.contactEmail || order.contactPhone || order.shippingAddress ) && (
								<Paper variant="outlined" sx={{ flex: 1, p: 2.5 }}>
									<Typography variant="h6" fontWeight={700} gutterBottom>Shipping</Typography>
									<InfoRow label="Contact Email" value={order.contactEmail} />
									<InfoRow label="Contact Phone" value={order.contactPhone} />
									{order.shippingAddress && (
										<InfoRow
											label="Address"
											value={[
												order.shippingAddress.line1,
												order.shippingAddress.line2,
												[ order.shippingAddress.city, order.shippingAddress.state ].filter( Boolean ).join( ', ' ),
												order.shippingAddress.postalCode,
											].filter( Boolean ).join( '\n' )}
										/>
									)}
								</Paper>
							)}
						</Stack>

						<Divider />

						{/* Line Items */}
						<Box>
							<Typography variant="h6" fontWeight={700} gutterBottom>
								Line Items{' '}
								<Typography component="span" variant="body2" color="text.secondary">
									({order.lineItems.length} items)
								</Typography>
							</Typography>

							<TableContainer component={Paper} variant="outlined">
								<Table size="small">
									<TableHead>
										<TableRow sx={{ bgcolor: 'grey.50' }}>
											<TableHeaderCell>Product</TableHeaderCell>
											<TableHeaderCell>Manufacturer #</TableHeaderCell>
											<TableHeaderCell>Vendor</TableHeaderCell>
											<TableHeaderCell align="right">Qty Requested</TableHeaderCell>
											<TableHeaderCell align="right">Qty Fulfilled</TableHeaderCell>
											<TableHeaderCell align="right">Qty Remaining</TableHeaderCell>
											<TableHeaderCell>Transfer Orders</TableHeaderCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{order.lineItems.length === 0 ? (
											<TableRow>
												<TableCell colSpan={7} align="center">
													<Typography variant="body2" color="text.secondary">No line items</Typography>
												</TableCell>
											</TableRow>
										) : (
											order.lineItems.map( ( item ) => {
												const transferOrders = item.fulfillmentLineItems
													.map( ( fli ) => fli.fulfillment.systemTransferOrder )
													.filter( Boolean )

												return (
													<TableRow key={item.id} hover>
														<TableCell>{item.product.name}</TableCell>
														<TableCell>{item.product.manufacturerNumber ?? '—'}</TableCell>
														<TableCell>{item.product.provider.name}</TableCell>
														<TableCell align="right">{item.quantity}</TableCell>
														<TableCell align="right">
															<Typography
																variant="body2"
																color={item.isFullyFulfilled ? 'success.main' : 'text.primary'}
																fontWeight={item.isFullyFulfilled ? 700 : 400}
															>
																{item.quantityFulfilled}
															</Typography>
														</TableCell>
														<TableCell align="right">
															<Typography
																variant="body2"
																color={item.quantityRemaining > 0 ? 'warning.main' : 'text.secondary'}
															>
																{item.quantityRemaining}
															</Typography>
														</TableCell>
														<TableCell>
															{transferOrders.length === 0 ? (
																<Typography variant="body2" color="text.secondary">—</Typography>
															) : (
																<Stack spacing={0.5}>
																	{transferOrders.map( ( to ) => to && (
																		<Stack key={to.id} direction="row" spacing={1} alignItems="center">
																			<RouterLink to={`/transfer-orders/${to.id}`} style={{ fontSize: 13 }}>
																				TO-{to.id}
																			</RouterLink>
																			<Chip
																				label={to.status}
																				color={TO_STATUS_COLOR[ to.status ] ?? 'default'}
																				size="small"
																				sx={{ fontSize: 10, height: 18 }}
																			/>
																		</Stack>
																	) )}
																</Stack>
															)}
														</TableCell>
													</TableRow>
												)
											} )
										)}
									</TableBody>
								</Table>
							</TableContainer>
						</Box>
					</Stack>
				)}
			</Box>
		</Box>
	)
}
